import {
  ArrowRightOnRectangleIcon,
  Bars3Icon,
  BellIcon,
  BanknotesIcon,
  BookOpenIcon,
  BuildingOffice2Icon,
  ChartBarIcon,
  ChevronDownIcon,
  Cog6ToothIcon,
  DocumentTextIcon,
  EnvelopeIcon,
  FolderIcon,
  HomeIcon,
  MagnifyingGlassIcon,
  MegaphoneIcon,
  PhoneIcon,
  RectangleStackIcon,
  UserGroupIcon,
  UsersIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { Menu, Transition } from '@headlessui/react';
import { Fragment, useState } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';

import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useProject, ALL_PROJECTS_ID } from '@/contexts/ProjectContext';
import { usePermissions } from '@/hooks/usePermissions';
import { Avatar, Spinner } from '@realvisor/ui';
import { SoftphonePanel } from '@/components/softphone/SoftphonePanel';
import type { Permission } from '@realvisor/shared';

// Helper component for project logo/color indicator
function ProjectIndicator({ project, size = 'sm' }: { project: { logoUrl?: string | null; color?: string; name: string; id: string }; size?: 'sm' | 'md' }) {
  const sizeClass = size === 'sm' ? 'h-4 w-4' : 'h-5 w-5';
  const textSize = size === 'sm' ? 'text-[6px]' : 'text-[8px]';
  
  if (project.logoUrl && project.logoUrl.startsWith('http')) {
    return (
      <img 
        src={project.logoUrl} 
        alt={project.name}
        className={`${sizeClass} rounded object-contain`}
      />
    );
  }
  
  return (
    <div 
      className={`${sizeClass} rounded flex items-center justify-center`}
      style={{ backgroundColor: project.color || '#3B82F6' }}
    >
      <span className={`text-white font-bold ${textSize}`}>
        {project.name.charAt(0).toUpperCase()}
      </span>
    </div>
  );
}

// Navigace rozdělená do sekcí podle funkce
type NavItem = { name: string; href: string; icon: any; permission?: Permission };
type NavSection = { label?: string; items: NavItem[] };

const topNavigationItems: NavItem[] = [
  { name: 'Projekty', href: '/projects', icon: FolderIcon, permission: 'projects:read' },
];

/** Default route per role — cc_operator lands on campaigns instead of dashboard */
export function getDefaultRoute(role: string): string {
  if (role === 'cc_operator') return '/callcenter/campaigns';
  return '/';
}

const navigationSections: NavSection[] = [
  {
    items: [
      { name: 'Dashboard', href: '/', icon: HomeIcon, permission: 'pipeline:read' },
      { name: 'Leady', href: '/leads', icon: UserGroupIcon, permission: 'leads:all' },
      { name: 'Kontakty', href: '/contacts', icon: UsersIcon, permission: 'contacts:all' },
      { name: 'Pipeline', href: '/pipeline', icon: RectangleStackIcon, permission: 'pipeline:read' },
    ],
  },
  {
    label: 'Byznys',
    items: [
      { name: 'Nemovitosti', href: '/properties', icon: BuildingOffice2Icon, permission: 'properties' },
      { name: 'Hypotéky', href: '/mortgages', icon: BanknotesIcon, permission: 'mortgages' },
    ],
  },
  {
    label: 'Marketing',
    items: [
      { name: 'Formuláře', href: '/forms', icon: DocumentTextIcon, permission: 'forms' },
      { name: 'Emaily', href: '/email/templates', icon: EnvelopeIcon, permission: 'email' },
    ],
  },
  {
    label: 'Call centrum',
    items: [
      { name: 'Kampaně', href: '/callcenter/campaigns', icon: MegaphoneIcon, permission: 'campaigns:read' },
      { name: 'Statistiky', href: '/callcenter/stats', icon: ChartBarIcon, permission: 'call_stats' },
      { name: 'Skripty', href: '/guides', icon: BookOpenIcon, permission: 'call_scripts:read' },
      { name: 'Tým', href: '/settings/users', icon: UsersIcon, permission: 'cc_users:manage' },
    ],
  },
];

export default function DashboardLayout() {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const { user, signOut } = useAuth();
  const { tenants, currentTenant, isLoading: tenantsLoading, switchTenant } = useTenant();
  const { projects, currentProject, isAllProjects, switchProject, getProjectColor } = useProject();
  const { can, isCallCenter } = usePermissions();
  const navigate = useNavigate();

  // Filter navigation based on user permissions
  const topNavigation = topNavigationItems.filter(
    (item) => !item.permission || can(item.permission),
  );
  const filteredSections = navigationSections
    .map((section) => ({
      ...section,
      items: section.items.filter((item) => !item.permission || can(item.permission)),
    }))
    .filter((section) => section.items.length > 0);

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch {
      toast.error('Nepodařilo se odhlásit');
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (searchQuery.trim()) {
      navigate(`/search?q=${encodeURIComponent(searchQuery)}`);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Mobile sidebar */}
      {sidebarOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-gray-900/80" onClick={() => setSidebarOpen(false)} />
          <div className="fixed inset-y-0 left-0 flex w-full max-w-xs flex-col bg-white">
            <div className="flex h-16 items-center justify-between px-6">
              <Link to="/" className="flex items-center gap-2">
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
                  <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M3 9L12 5L21 9V15L12 19L3 15V9Z" />
                    <path d="M12 5V19" />
                  </svg>
                </div>
                <span className="text-lg font-bold text-gray-900">RealVisor</span>
              </Link>
              <button onClick={() => setSidebarOpen(false)} className="text-gray-500">
                <XMarkIcon className="h-6 w-6" />
              </button>
            </div>

            {/* Mobile Tenant Selector */}
            {tenants.length > 1 && (
              <div className="px-3 py-2">
                <Menu as="div" className="relative">
                  <Menu.Button className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                    <span className="truncate font-medium">{currentTenant?.name || 'Vyberte tenant'}</span>
                    <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute left-0 right-0 z-10 mt-1 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                      {tenants.map((tenant) => (
                        <Menu.Item key={tenant.id}>
                          {({ active }) => (
                            <button
                              onClick={() => switchTenant(tenant.id)}
                              className={`flex w-full items-center px-3 py-2 text-sm ${
                                active ? 'bg-gray-50' : ''
                              } ${tenant.id === currentTenant?.id ? 'font-medium text-blue-600' : 'text-gray-700'}`}
                            >
                              {tenant.name}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            )}

            <nav className="flex-1 space-y-1 px-3 py-4">
              {/* Projekty nahoře */}
              {topNavigation.map((item) => (
                <NavLink
                  key={item.name}
                  to={item.href}
                  onClick={() => setSidebarOpen(false)}
                  className={({ isActive }) =>
                    `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium ${
                      isActive
                        ? 'bg-blue-50 text-blue-600'
                        : 'text-gray-700 hover:bg-gray-100'
                    }`
                  }
                >
                  <item.icon className="h-5 w-5" />
                  {item.name}
                </NavLink>
              ))}
              
              {/* Mobile Project Selector - mezi Projekty a zbytkem */}
              {projects.length > 0 && (
                <div className="py-2">
                  <label className="block text-xs font-medium text-gray-500 mb-1 px-1">Aktivní projekt</label>
                  <Menu as="div" className="relative">
                    <Menu.Button className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm">
                      <div className="flex items-center gap-2 truncate">
                        {isAllProjects ? (
                          <div className="h-3 w-3 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />
                        ) : currentProject ? (
                          <ProjectIndicator project={currentProject} size="sm" />
                        ) : null}
                        <span className="truncate font-medium">
                          {isAllProjects ? 'Všechny projekty' : currentProject?.name}
                        </span>
                      </div>
                      <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                    </Menu.Button>
                    <Transition
                      as={Fragment}
                      enter="transition ease-out duration-100"
                      enterFrom="transform opacity-0 scale-95"
                      enterTo="transform opacity-100 scale-100"
                      leave="transition ease-in duration-75"
                      leaveFrom="transform opacity-100 scale-100"
                      leaveTo="transform opacity-0 scale-95"
                    >
                      <Menu.Items className="absolute left-0 right-0 z-10 mt-1 rounded-lg border border-gray-200 bg-white py-1 shadow-lg max-h-60 overflow-auto">
                        <Menu.Item>
                          {({ active }) => (
                            <button
                              onClick={() => switchProject(ALL_PROJECTS_ID)}
                              className={`flex w-full items-center gap-2 px-3 py-2 text-sm ${
                                active ? 'bg-gray-50' : ''
                              } ${isAllProjects ? 'font-medium text-blue-600' : 'text-gray-700'}`}
                            >
                              <div className="h-3 w-3 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />
                              Všechny projekty
                            </button>
                          )}
                        </Menu.Item>
                        <div className="border-t border-gray-100 my-1" />
                        {projects.map((project) => (
                          <Menu.Item key={project.id}>
                            {({ active }) => (
                              <button
                                onClick={() => switchProject(project.id)}
                                className={`flex w-full items-center gap-2 px-3 py-2 text-sm ${
                                  active ? 'bg-gray-50' : ''
                                } ${project.id === currentProject?.id ? 'font-medium text-blue-600' : 'text-gray-700'}`}
                              >
                                <ProjectIndicator project={project} size="sm" />
                                {project.name}
                              </button>
                            )}
                          </Menu.Item>
                        ))}
                      </Menu.Items>
                    </Transition>
                  </Menu>
                </div>
              )}
              
              {/* Navigace podle sekcí */}
              {filteredSections.map((section, idx) => (
                <div key={section.label || idx}>
                  {idx > 0 && <div className="border-t border-gray-200 my-2" />}
                  {section.label && (
                    <p className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                      {section.label}
                    </p>
                  )}
                  {section.items.map((item) => (
                    <NavLink
                      key={item.name}
                      to={item.href}
                      onClick={() => setSidebarOpen(false)}
                      className={({ isActive }) =>
                        `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium ${
                          isActive
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-gray-700 hover:bg-gray-100'
                        }`
                      }
                    >
                      <item.icon className="h-5 w-5" />
                      {item.name}
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
          </div>
        </div>
      )}

      {/* Desktop sidebar */}
      <div className="hidden lg:fixed lg:inset-y-0 lg:flex lg:w-64 lg:flex-col">
        <div className="flex flex-1 flex-col border-r border-gray-200 bg-white">
          <div className="flex h-16 items-center px-6">
            <Link to="/" className="flex items-center gap-2">
              <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-blue-600">
                <svg className="h-5 w-5 text-white" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M3 9L12 5L21 9V15L12 19L3 15V9Z" />
                  <path d="M12 5V19" />
                </svg>
              </div>
              <span className="text-lg font-bold text-gray-900">RealVisor</span>
            </Link>
          </div>

          {/* Desktop Tenant Selector */}
          {tenants.length > 1 && (
            <div className="px-3 py-2">
              <Menu as="div" className="relative">
                <Menu.Button className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm hover:bg-gray-100">
                  <span className="truncate font-medium">{currentTenant?.name || 'Vyberte tenant'}</span>
                  <ChevronDownIcon className="h-4 w-4 text-gray-500" />
                </Menu.Button>
                <Transition
                  as={Fragment}
                  enter="transition ease-out duration-100"
                  enterFrom="transform opacity-0 scale-95"
                  enterTo="transform opacity-100 scale-100"
                  leave="transition ease-in duration-75"
                  leaveFrom="transform opacity-100 scale-100"
                  leaveTo="transform opacity-0 scale-95"
                >
                  <Menu.Items className="absolute left-0 right-0 z-10 mt-1 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                    {tenants.map((tenant) => (
                      <Menu.Item key={tenant.id}>
                        {({ active }) => (
                          <button
                            onClick={() => switchTenant(tenant.id)}
                            className={`flex w-full items-center px-3 py-2 text-sm ${
                              active ? 'bg-gray-50' : ''
                            } ${tenant.id === currentTenant?.id ? 'font-medium text-blue-600' : 'text-gray-700'}`}
                          >
                            {tenant.name}
                          </button>
                        )}
                      </Menu.Item>
                    ))}
                  </Menu.Items>
                </Transition>
              </Menu>
            </div>
          )}

          <nav className="flex-1 space-y-1 px-3 py-4">
            {/* Projekty nahoře */}
            {topNavigation.map((item) => (
              <NavLink
                key={item.name}
                to={item.href}
                className={({ isActive }) =>
                  `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-blue-50 text-blue-600'
                      : 'text-gray-700 hover:bg-gray-100'
                  }`
                }
              >
                <item.icon className="h-5 w-5" />
                {item.name}
              </NavLink>
            ))}
            
            {/* Desktop Project Selector - mezi Projekty a zbytkem */}
            {projects.length > 0 && (
              <div className="py-2">
                <label className="block text-xs font-medium text-gray-500 mb-1 px-1">Aktivní projekt</label>
                <Menu as="div" className="relative">
                  <Menu.Button className="flex w-full items-center justify-between rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-sm hover:bg-gray-100">
                    <div className="flex items-center gap-2 truncate">
                      {isAllProjects ? (
                        <div className="h-3 w-3 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />
                      ) : currentProject ? (
                        <ProjectIndicator project={currentProject} size="sm" />
                      ) : null}
                      <span className="truncate font-medium">
                        {isAllProjects ? 'Všechny projekty' : currentProject?.name}
                      </span>
                    </div>
                    <ChevronDownIcon className="h-4 w-4 text-gray-500 flex-shrink-0" />
                  </Menu.Button>
                  <Transition
                    as={Fragment}
                    enter="transition ease-out duration-100"
                    enterFrom="transform opacity-0 scale-95"
                    enterTo="transform opacity-100 scale-100"
                    leave="transition ease-in duration-75"
                    leaveFrom="transform opacity-100 scale-100"
                    leaveTo="transform opacity-0 scale-95"
                  >
                    <Menu.Items className="absolute left-0 right-0 z-10 mt-1 rounded-lg border border-gray-200 bg-white py-1 shadow-lg max-h-60 overflow-auto">
                      {/* Všechny projekty */}
                      <Menu.Item>
                        {({ active }) => (
                          <button
                            onClick={() => switchProject(ALL_PROJECTS_ID)}
                            className={`flex w-full items-center gap-2 px-3 py-2 text-sm ${
                              active ? 'bg-gray-50' : ''
                            } ${isAllProjects ? 'font-medium text-blue-600' : 'text-gray-700'}`}
                          >
                            <div className="h-3 w-3 rounded-full bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500" />
                            Všechny projekty
                          </button>
                        )}
                      </Menu.Item>
                      <div className="border-t border-gray-100 my-1" />
                      {/* Jednotlivé projekty */}
                      {projects.map((project) => (
                        <Menu.Item key={project.id}>
                          {({ active }) => (
                            <button
                              onClick={() => switchProject(project.id)}
                              className={`flex w-full items-center gap-2 px-3 py-2 text-sm ${
                                active ? 'bg-gray-50' : ''
                              } ${project.id === currentProject?.id ? 'font-medium text-blue-600' : 'text-gray-700'}`}
                            >
                              <ProjectIndicator project={project} size="sm" />
                              {project.name}
                            </button>
                          )}
                        </Menu.Item>
                      ))}
                    </Menu.Items>
                  </Transition>
                </Menu>
              </div>
            )}
            
            {/* Navigace podle sekcí */}
            {filteredSections.map((section, idx) => (
              <div key={section.label || idx}>
                {idx > 0 && <div className="border-t border-gray-200 my-2" />}
                {section.label && (
                  <p className="px-3 pt-2 pb-1 text-xs font-semibold uppercase tracking-wider text-gray-400">
                    {section.label}
                  </p>
                )}
                {section.items.map((item) => (
                  <NavLink
                    key={item.name}
                    to={item.href}
                    className={({ isActive }) =>
                      `flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors ${
                        isActive
                          ? 'bg-blue-50 text-blue-600'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`
                    }
                  >
                    <item.icon className="h-5 w-5" />
                    {item.name}
                  </NavLink>
                ))}
              </div>
            ))}
          </nav>

          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center gap-3">
              <Avatar name={user?.user_metadata?.['full_name'] || user?.email} size="sm" />
              <div className="flex-1 min-w-0">
                <p className="truncate text-sm font-medium text-gray-900">
                  {user?.user_metadata?.['full_name'] || 'Uživatel'}
                </p>
                <p className="truncate text-xs text-gray-500">{user?.email}</p>
              </div>
            </div>
            <div className="mt-4 flex gap-2">
              {can('settings') && (
                <Link
                  to="/settings"
                  className="flex flex-1 items-center justify-center gap-2 rounded-lg border border-gray-200 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <Cog6ToothIcon className="h-4 w-4" />
                  Nastavení
                </Link>
              )}
              <button
                onClick={handleSignOut}
                className="flex items-center justify-center rounded-lg border border-gray-200 px-3 py-2 text-gray-700 hover:bg-gray-50 transition-colors"
                title="Odhlásit se"
              >
                <ArrowRightOnRectangleIcon className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar */}
        <div className="sticky top-0 z-40 flex h-16 items-center gap-4 border-b border-gray-200 bg-white px-4 lg:px-8">
          <button
            onClick={() => setSidebarOpen(true)}
            className="text-gray-500 lg:hidden"
          >
            <Bars3Icon className="h-6 w-6" />
          </button>

          {/* Search */}
          <form onSubmit={handleSearch} className="hidden lg:flex flex-1 max-w-md">
            <div className="relative w-full">
              <MagnifyingGlassIcon className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Hledat leady, kontakty..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full rounded-lg border border-gray-200 bg-gray-50 py-2 pl-10 pr-4 text-sm placeholder-gray-500 focus:border-blue-500 focus:bg-white focus:outline-none focus:ring-1 focus:ring-blue-500"
              />
            </div>
          </form>

          <div className="flex-1 lg:hidden" />

          {/* Right side */}
          <div className="flex items-center gap-3">
            {/* Notifications */}
            <button className="relative rounded-lg p-2 text-gray-500 hover:bg-gray-100 transition-colors">
              <BellIcon className="h-5 w-5" />
              <span className="absolute right-1 top-1 flex h-2 w-2">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-blue-400 opacity-75"></span>
                <span className="relative inline-flex h-2 w-2 rounded-full bg-blue-500"></span>
              </span>
            </button>

            {/* User menu (mobile) */}
            <Menu as="div" className="relative lg:hidden">
              <Menu.Button>
                <Avatar name={user?.user_metadata?.['full_name'] || user?.email} size="sm" />
              </Menu.Button>
              <Transition
                as={Fragment}
                enter="transition ease-out duration-100"
                enterFrom="transform opacity-0 scale-95"
                enterTo="transform opacity-100 scale-100"
                leave="transition ease-in duration-75"
                leaveFrom="transform opacity-100 scale-100"
                leaveTo="transform opacity-0 scale-95"
              >
                <Menu.Items className="absolute right-0 z-10 mt-2 w-48 rounded-lg border border-gray-200 bg-white py-1 shadow-lg">
                  <Menu.Item>
                    {({ active }) => (
                      <Link
                        to="/settings"
                        className={`flex items-center gap-2 px-4 py-2 text-sm ${active ? 'bg-gray-50' : ''} text-gray-700`}
                      >
                        <Cog6ToothIcon className="h-4 w-4" />
                        Nastavení
                      </Link>
                    )}
                  </Menu.Item>
                  <Menu.Item>
                    {({ active }) => (
                      <button
                        onClick={handleSignOut}
                        className={`flex w-full items-center gap-2 px-4 py-2 text-sm ${active ? 'bg-gray-50' : ''} text-gray-700`}
                      >
                        <ArrowRightOnRectangleIcon className="h-4 w-4" />
                        Odhlásit se
                      </button>
                    )}
                  </Menu.Item>
                </Menu.Items>
              </Transition>
            </Menu>
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>

        {/* Footer */}
        <footer className="border-t border-gray-200 bg-white px-4 py-6 lg:px-8">
          <div className="text-center text-sm text-gray-500">
            © {new Date().getFullYear()} RealVisor. Design & Development by <span className="font-medium text-gray-700">David Choc</span>
          </div>
        </footer>
      </div>

      {/* SIP Softphone */}
      <SoftphonePanel />
    </div>
  );
}
